import { useInfiniteQuery, useQuery, UseQueryOptions } from '@tanstack/react-query'
import qs from 'qs'
import { ModuleType } from '~/api/jobs'

import fetch from '~/common/fetch'
import { OffsetLimitPaginationResponse } from './common'

export interface JobTemplateResponse {
  id: number
  title: string
  description?: string
  job_category?: number | null
  is_popular_job: boolean
  created_by: number
  updated_by: number
  job_code?: number | null
  sca_job_code?: number | null
  business_unit?: number | null
  approvals_required: number
  must_have_skills: number[]
  nice_to_have_skills: number[]
  job_types: JobTemplateJobType[]
  apply_shifts: boolean
  use_fast_path?: boolean
  job_category_display?: string
}

export interface JobTemplateJobType {
  id: number
  name: string
}

export type ListJobTemplateResponse = JobTemplateResponse[]

export type JobTemplatesQueryParams = {
  is_active?: boolean
  all?: true
  is_popular_job?: boolean
  title?: string
  countries?: string
}

const JOB_TEMPLATE_URL = '/api/v2/job_titles/titles'
const PAGE_LIMIT = 30

export const jobTemplateApi = {
  get: (templateId: number): Promise<JobTemplateResponse> => fetch.get(`${JOB_TEMPLATE_URL}/${templateId}`),
  list_paginated(
    queryParams: JobTemplatesQueryParams,
    offset: number
  ): Promise<OffsetLimitPaginationResponse<JobTemplateResponse>> {
    // we'd only ever want contigent so hard coding it here
    return fetch.get(
      `${JOB_TEMPLATE_URL}?${qs.stringify({
        ...queryParams,
        limit: PAGE_LIMIT,
        offset,
        modules: ModuleType.CONTINGENT
      })}`
    )
  },
  // TODO: verify that this is not going to be used at all and REMOVE(should use paginated query above!)
  list_all: (): Promise<ListJobTemplateResponse> => fetch.get(`${JOB_TEMPLATE_URL}?all=true`),
  getCountryRestrictions: (templateId: number): Promise<string[]> =>
    fetch.get(`${JOB_TEMPLATE_URL}/${templateId}/get_country_restrictions`)
}

const JOB_TEMPLATE_QUERY = 'job-templates' as const
export const useJobTemplateGetQuery = (id: number | null, options: UseQueryOptions<JobTemplateResponse> = {}) =>
  useQuery({ queryKey: [JOB_TEMPLATE_QUERY, id], queryFn: () => jobTemplateApi.get(id!), ...options, enabled: !!id })

export const useJobTemplateListQuery = () =>
  useQuery({ queryKey: [JOB_TEMPLATE_QUERY], queryFn: () => jobTemplateApi.list_all() })

export const useJobTemplateListInfiniteQuery = (queryParams: JobTemplatesQueryParams) =>
  useInfiniteQuery({
    queryKey: [JOB_TEMPLATE_QUERY, queryParams],
    queryFn: ({ pageParam = 0 }) => jobTemplateApi.list_paginated(queryParams, Number(pageParam)),
    getNextPageParam: lastPage => lastPage.next_params?.offset ?? false
  })

const JOB_TEMPLATE_COUNTRY_RESTRICTIONS = 'job-template-country-restrictions'
export const useJobTemplateCountryRestrictionsQuery = (id: number | null) =>
  useQuery({
    queryKey: [JOB_TEMPLATE_COUNTRY_RESTRICTIONS, id],
    queryFn: () => jobTemplateApi.getCountryRestrictions(id!),
    enabled: id != null && Number.isFinite(id)
  })
